import React, { useMemo } from 'react';
import { Modal, Button, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import ViewPdfButton from './ViewPdfButton';

const badgeVariant = (status) => {
  switch (status) {
    case 'TERMINATED': return 'success';
    default: return 'secondary';
  }
};

export default function MoncashViewModal({ show, onHide, row, loading = false, error = '' }) {
  const typeLabel = useMemo(() => {
    const t = String(row?.type || '').toUpperCase();
    if (t === 'DEPOSIT') return 'Dépôt';
    if (t === 'WITHDRAWAL') return 'Retrait';
    return '—';
  }, [row]);

  const clientLabel = useMemo(() => {
    if (row?.client) {
      return `${row.client.firstName || ''} ${row.client.lastName || ''}`.trim();
    }
    return row?.clientName || 'Non-client';
  }, [row]);

  const getDocument = () => ({
    title: `Détails MonCash${row?.id ? ` - #${row.id}` : ''}`,
    sections: [
      {
        title: 'Informations générales',
        rows: [
          { label: 'Type', value: typeLabel },
          { label: 'Téléphone', value: row?.phone || '—' },
          { label: 'Client', value: clientLabel },
          { label: 'Statut', value: row?.status || '—' },
          { label: 'Créée le', value: row?.createdAt ? new Date(row.createdAt).toLocaleString() : '—' },
          { label: 'Créée par', value: row?.createdByUser?.username || '—' },
          { label: 'Modifiée par', value: row?.modifiedByUser?.username || '—' },
          { label: 'Date modification', value: row?.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : '—' },
        ],
      },
      {
        title: 'Montants',
        rows: [
          { label: 'Montant saisi', value: `${Number(row?.amountInputHTG || 0)} HTG` },
          { label: 'Frais', value: `${Number(row?.feeHTG || 0)} HTG` },
          { label: 'Frais y compris', value: row?.feeIncluded ? 'Oui' : 'Non' },
          {
            label: String(row?.type || '').toUpperCase() === 'DEPOSIT' ? 'Crédit MonCash' : 'Débit MonCash',
            value: `${Number(row?.amountAccountHTG || 0)} HTG`,
          },
          {
            label: String(row?.type || '').toUpperCase() === 'DEPOSIT' ? 'Cash encaissé' : 'Cash décaissé',
            value: `${Number(row?.amountCashHTG || 0)} HTG`,
          },
        ],
      },
      {
        title: 'Note',
        rows: [
          { label: 'Note', value: row?.note || '—' },
        ],
      },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Détails MonCash</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <Alert variant="secondary" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <div>Chargement…</div>
          </Alert>
        )}

        {!!error && <Alert variant="danger">{error}</Alert>}

        {!loading && !row && !error && <Alert variant="secondary">Aucune donnée à afficher.</Alert>}

        {!!row && (
          <>
            <Alert variant="secondary" className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-semibold">{typeLabel}</div>
                <div className="text-muted small">Téléphone: {row.phone || '—'}</div>
              </div>
              <Badge bg={badgeVariant(row.status)}>{row.status || '—'}</Badge>
            </Alert>

            <Row className="g-3">
              <Col md={6}>
                <div className="text-muted small">Client</div>
                <div className="fw-semibold">{clientLabel}</div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">Créée le</div>
                <div className="fw-semibold">
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                </div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">Créée par</div>
                <div className="fw-semibold">{row.createdByUser?.username || '—'}</div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">Modifiée par</div>
                <div className="fw-semibold">{row.modifiedByUser?.username || '—'}</div>
                <div className="text-muted small">
                  {row.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : '—'}
                </div>
              </Col>

              <Col md={4}>
                <div className="text-muted small">Montant saisi</div>
                <div className="fw-semibold">{Number(row.amountInputHTG || 0)} HTG</div>
              </Col>

              <Col md={4}>
                <div className="text-muted small">Frais</div>
                <div className="fw-semibold">{Number(row.feeHTG || 0)} HTG</div>
              </Col>

              <Col md={4}>
                <div className="text-muted small">Frais y compris</div>
                <div className="fw-semibold">{row.feeIncluded ? 'Oui' : 'Non'}</div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">
                  {String(row.type || '').toUpperCase() === 'DEPOSIT' ? 'Crédit MonCash' : 'Débit MonCash'}
                </div>
                <div className="fw-semibold">{Number(row.amountAccountHTG || 0)} HTG</div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">
                  {String(row.type || '').toUpperCase() === 'DEPOSIT' ? 'Cash encaissé' : 'Cash décaissé'}
                </div>
                <div className="fw-semibold">{Number(row.amountCashHTG || 0)} HTG</div>
              </Col>

              <Col md={12}>
                <div className="text-muted small">Note</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{row.note || '—'}</div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!!row && <ViewPdfButton getDocument={getDocument} className="me-2" />}
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}